import { createContext, useContext, useEffect, useState, useMemo, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  address: string | null;
  default_currency: string;
  default_language: string;
  timezone: string;
  app_icon_url: string | null;
  settings: Record<string, any>;
  max_slots_per_booking: number;
  max_concurrent_bookings: number;
  member_max_slots_per_booking: number;
  member_max_concurrent_bookings: number;
  // PR-AL: sentinel auth.users id for admin-created walk-in bookings.
  // Auto-populated on org INSERT via trg_ensure_walk_in_user. Always
  // non-null after migration 20260427120000_walk_in_sentinel_user.
  walk_in_user_id: string;
}

interface OrganizationContextType {
  organization: Organization | null;
  isLoading: boolean;
}

const OrganizationContext = createContext<OrganizationContextType>({
  organization: null,
  isLoading: true,
});

function toOrg(row: any): Organization {
  return { ...row, settings: (row.settings as Record<string, any>) ?? {} };
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setOrganization(null);
      setIsLoading(false);
      return;
    }

    const fetchOrg = async () => {
      try {
        setIsLoading(true);

        // Resolve the current user's org via organization_members (canonical source,
        // matches get_user_org_ids() RPC + AdminLeagues.tsx pattern). If the user has
        // memberships on multiple orgs we pick the earliest — good enough for now;
        // an org-switcher UI is separately planned.
        const { data: { user } } = await supabase.auth.getUser();

        let query = supabase.from("organizations").select("*");

        if (user) {
          const { data: membership } = await supabase
            .from("organization_members")
            .select("organization_id")
            .eq("user_id", user.id)
            .order("created_at", { ascending: true })
            .limit(1)
            .maybeSingle();

          if (membership?.organization_id) {
            query = query.eq("id", membership.organization_id);
          } else {
            // Authenticated user without an organization_members row — fall through
            // to the legacy "first org" behavior so the app doesn't brick.
            query = query.order("created_at", { ascending: true });
          }
        } else {
          query = query.order("created_at", { ascending: true });
        }

        const { data: org, error } = await query.limit(1).maybeSingle();

        if (error) throw error;

        if (org) {
          setOrganization(toOrg(org));
        } else {
          setOrganization(null);
        }
      } catch (error) {
        console.error("Failed to fetch organization:", error);
        setOrganization(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrg();
  }, [isAuthenticated]);

  // PR-AM: document.title is now owned by usePublicTitle (called in
  // HostnameGate, sourced from PublicOrgContext). Removed the duplicate
  // title-setting here so there's a single source of truth — and so the
  // title doesn't briefly flicker when an authenticated user has a
  // different membership-org name than the hostname-resolved org.

  const contextValue = useMemo(() => ({ organization, isLoading }), [organization, isLoading]);

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  return useContext(OrganizationContext);
}
